import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  collection,
  updateDoc,
  doc,
  serverTimestamp,
  getDoc,
  setDoc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import type { AppDispatch } from "./store";

// ═══════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════

export type RiderReservationStatus =
  | "pending"
  | "confirmed"
  | "active"
  | "completed"
  | "cancelled"
  | "no_show";

export type RiderTripType =
  | "airport_transfer"
  | "city_ride"
  | "intercity"
  | "train_transfer"
  | "charter"
  | "hourly"
  | "custom";

export interface RiderReservation {
  id: string;
  riderId: string;        // the passenger / guest
  driverId: string;       // the driver / host
  vehicleId?: string;     // optional linked vehicle
  paymentId: string;      // Stripe session / payment ID — used as doc ID

  // ── Journey ──
  type: RiderTripType;
  origin: string;
  destination: string;
  departureDate: string;   // ISO date string "YYYY-MM-DD"
  departureTime?: string;  // "HH:MM"
  estimatedArrival?: string;
  passengers: number;
  luggageCount?: number;
  notes?: string;

  // ── Pricing ──
  totalPaid: number;
  baseFare: number;
  riderFee: number;
  driverFee: number;
  driverProfit: number;
  platformProfit: number;
  extras?: Record<string, any>;
  extraCharges?: number;

  // ── Status flags ──
  status: RiderReservationStatus;
  isConfirmed: boolean;
  isCancelled: boolean;
  isRefunded: boolean;
  isPaid: boolean;
  hasBoarded: boolean;

  // ── Derived flags (computed client-side) ──
  isUpcoming: boolean;
  isArrivingSoon: boolean;   // departure ≤ 3 days away
  isOngoing: boolean;        // driver has started the trip
  isCompleted: boolean;
  isCheckingOut: boolean;    // trip ended, within 7-day review window

  createdAt?: any;
  updatedAt?: any;

  [key: string]: any;
}

export interface RiderCredit {
  riderId: string;
  credits: number;
  lastCreditedAt?: any;
  lastUpdated?: any;
  refunded?: boolean;
  refundedAt?: any;
}

// ═══════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════

/**
 * Mirrors calculateStatusFlags from shortstayReservationSlice
 * but works off a single departureDate (point-in-time trip rather than range).
 */
export const calculateRiderStatusFlags = (
  departureDate: string,
  estimatedArrival?: string
) => {
  const now   = new Date();
  const today = new Date(now.toDateString()); // strip time

  const depDate = new Date(departureDate);
  depDate.setHours(0, 0, 0, 0);

  const arrDate = estimatedArrival
    ? new Date(estimatedArrival)
    : new Date(depDate.getTime() + 86400000); // fallback: dep + 1 day
  arrDate.setHours(0, 0, 0, 0);

  const diffToDep =
    (depDate.getTime() - today.getTime()) / (1000 * 3600 * 24);

  const diffFromArr =
    (today.getTime() - arrDate.getTime()) / (1000 * 3600 * 24);

  const isCompleted   = diffFromArr > 7;
  const isCheckingOut = diffFromArr >= 0 && diffFromArr <= 7;

  return {
    isUpcoming:      diffToDep > 3,
    isArrivingSoon:  diffToDep <= 3 && diffToDep > 0,
    isOngoing:       today >= depDate && today < arrDate,
    isCompleted,
    isCheckingOut,
  };
};

// ═══════════════════════════════════════════════════════════
// UPSERT — idempotent, paymentId as doc ID
// ═══════════════════════════════════════════════════════════

export const upsertRiderReservation = createAsyncThunk<
  RiderReservation,
  Partial<RiderReservation> & { paymentId: string },
  { rejectValue: string }
>(
  "riderReservations/upsert",
  async (data, { rejectWithValue }) => {
    try {
      if (!data.paymentId) throw new Error("Missing paymentId");

      const reservationId = data.paymentId;
      const docRef = doc(db, "riderReservations", reservationId);

      const flags = calculateRiderStatusFlags(
        data.departureDate!,
        data.estimatedArrival
      );

      const existingSnap = await getDoc(docRef);

      if (existingSnap.exists()) {
        await updateDoc(docRef, {
          ...data,
          ...flags,
          updatedAt: serverTimestamp(),
        });

        const existing = existingSnap.data() as RiderReservation;
        const { id: _a, ...restExisting } = existing;
        const { id: _b, ...restData }     = data as any;

        return {
          id: reservationId,
          ...restExisting,
          ...restData,
          ...flags,
        } as RiderReservation;
      } else {
        const payload: Omit<RiderReservation, "id"> = {
          riderId:         data.riderId!,
          driverId:        data.driverId!,
          vehicleId:       data.vehicleId,
          paymentId:       data.paymentId,
          type:            data.type ?? "city_ride",
          origin:          data.origin ?? "",
          destination:     data.destination ?? "",
          departureDate:   data.departureDate!,
          departureTime:   data.departureTime,
          estimatedArrival:data.estimatedArrival,
          passengers:      data.passengers ?? 1,
          luggageCount:    data.luggageCount ?? 0,
          notes:           data.notes,
          totalPaid:       data.totalPaid ?? 0,
          baseFare:        data.baseFare ?? 0,
          riderFee:        data.riderFee ?? 0,
          driverFee:       data.driverFee ?? 0,
          driverProfit:    data.driverProfit ?? 0,
          platformProfit:  data.platformProfit ?? 0,
          extras:          data.extras ?? {},
          extraCharges:    data.extraCharges ?? 0,
          status:          "confirmed",
          isConfirmed:     true,
          isCancelled:     false,
          isRefunded:      false,
          isPaid:          false,
          hasBoarded:      false,
          ...flags,
          createdAt:       serverTimestamp(),
          updatedAt:       serverTimestamp(),
        };

        await setDoc(docRef, payload, { merge: true });
        return { id: reservationId, ...payload } as RiderReservation;
      }
    } catch (err: any) {
      console.error("🔥 upsertRiderReservation error:", err);
      return rejectWithValue(err.message ?? "Failed to upsert rider reservation");
    }
  }
);

// ═══════════════════════════════════════════════════════════
// CHECK DRIVER AVAILABILITY
// ═══════════════════════════════════════════════════════════

export const checkRiderDateAvailability = createAsyncThunk<
  { available: boolean; conflict?: RiderReservation },
  { driverId: string; departureDate: string },
  { rejectValue: string }
>(
  "riderReservations/checkAvailability",
  async ({ driverId, departureDate }, { rejectWithValue }) => {
    try {
      const q = query(
        collection(db, "riderReservations"),
        where("driverId", "==", driverId),
        where("departureDate", "==", departureDate)
      );
      const snap = await getDocs(q);

      for (const docSnap of snap.docs) {
        const d = docSnap.data() as RiderReservation;
        const isActive =
          d.isConfirmed && !d.isCancelled && !d.isRefunded;

        if (isActive) {
          return { available: false, conflict: { ...d, id: docSnap.id } };
        }
      }

      return { available: true };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ═══════════════════════════════════════════════════════════
// RESCHEDULE
// ═══════════════════════════════════════════════════════════

export const rescheduleRiderReservation = createAsyncThunk<
  RiderReservation,
  { reservationId: string; newDepartureDate: string; newDepartureTime?: string; newEstimatedArrival?: string },
  { rejectValue: string }
>(
  "riderReservations/reschedule",
  async (
    { reservationId, newDepartureDate, newDepartureTime, newEstimatedArrival },
    { rejectWithValue }
  ) => {
    try {
      const ref  = doc(db, "riderReservations", reservationId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("Reservation not found");

      const existing = snap.data() as RiderReservation;
      const flags    = calculateRiderStatusFlags(newDepartureDate, newEstimatedArrival);

      await updateDoc(ref, {
        departureDate:    newDepartureDate,
        departureTime:    newDepartureTime ?? existing.departureTime,
        estimatedArrival: newEstimatedArrival ?? existing.estimatedArrival,
        ...flags,
        updatedAt: serverTimestamp(),
      });

      return {
        ...existing,
        id:               reservationId,
        departureDate:    newDepartureDate,
        departureTime:    newDepartureTime ?? existing.departureTime,
        estimatedArrival: newEstimatedArrival ?? existing.estimatedArrival,
        ...flags,
      } as RiderReservation;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ═══════════════════════════════════════════════════════════
// CANCEL
// ═══════════════════════════════════════════════════════════

export const cancelRiderReservation = createAsyncThunk<
  string,
  { reservationId: string },
  { rejectValue: string }
>(
  "riderReservations/cancel",
  async ({ reservationId }, { rejectWithValue }) => {
    try {
      const ref = doc(db, "riderReservations", reservationId);
      await updateDoc(ref, {
        isCancelled: true,
        status:      "cancelled",
        updatedAt:   serverTimestamp(),
      });
      return reservationId;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ═══════════════════════════════════════════════════════════
// REAL-TIME LISTENERS
// ═══════════════════════════════════════════════════════════

/** Listen to all reservations for a rider (passenger) — used in Home.tsx */
export const listenToRiderReservations =
  (riderId: string) => (dispatch: AppDispatch) => {
    if (!riderId) return;

    const q = query(
      collection(db, "riderReservations"),
      where("riderId", "==", riderId)
    );

    return onSnapshot(q, (snapshot) => {
      const reservations: RiderReservation[] = snapshot.docs.map((docSnap) => {
        const data  = docSnap.data() as Omit<RiderReservation, "id">;
        const flags = calculateRiderStatusFlags(
          data.departureDate,
          data.estimatedArrival
        );
        return { ...data, id: docSnap.id, ...flags } as RiderReservation;
      });

      dispatch(setRiderReservations(reservations));
    });
  };

/** Listen to all reservations for a driver */
export const listenToDriverReservations =
  (driverId: string) => (dispatch: AppDispatch) => {
    if (!driverId) return;

    const q = query(
      collection(db, "riderReservations"),
      where("driverId", "==", driverId)
    );

    return onSnapshot(q, (snapshot) => {
      const reservations: RiderReservation[] = snapshot.docs.map((docSnap) => {
        const data  = docSnap.data() as Omit<RiderReservation, "id">;
        const flags = calculateRiderStatusFlags(
          data.departureDate,
          data.estimatedArrival
        );
        return { ...data, id: docSnap.id, ...flags } as RiderReservation;
      });

      dispatch(setDriverReservations(reservations));
    });
  };

/** Listen to a single reservation by ID */
export const listenToRiderReservationById =
  (reservationId: string) => (dispatch: AppDispatch) => {
    if (!reservationId) return;

    const ref = doc(db, "riderReservations", reservationId);

    return onSnapshot(ref, (docSnap) => {
      if (docSnap.exists()) {
        const data  = docSnap.data() as Omit<RiderReservation, "id">;
        const flags = calculateRiderStatusFlags(
          data.departureDate,
          data.estimatedArrival
        );
        dispatch(
          setReservationById({ ...data, id: docSnap.id, ...flags } as RiderReservation)
        );
      } else {
        dispatch(setReservationById(null));
      }
    });
  };

/** Global listener — all reservations (admin / management view) */
export const listenToAllRiderReservations =
  () => (dispatch: AppDispatch) => {
    const ref = collection(db, "riderReservations");

    return onSnapshot(ref, (snapshot) => {
      const reservations: RiderReservation[] = snapshot.docs.map((docSnap) => {
        const data  = docSnap.data() as Omit<RiderReservation, "id">;
        const flags = calculateRiderStatusFlags(
          data.departureDate,
          data.estimatedArrival
        );
        return { ...data, id: docSnap.id, ...flags } as RiderReservation;
      });

      dispatch(setAllReservations(reservations));
    });
  };

/** Listen to real-time credits for a rider */
export const listenToRiderCredit =
  (riderId: string) => (dispatch: AppDispatch) => {
    if (!riderId) return;

    const ref = doc(db, "riderCredits", riderId);

    return onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        dispatch(setRiderCredit({ riderId, credits: data.credits ?? 0 }));
      } else {
        dispatch(setRiderCredit({ riderId, credits: 0 }));
      }
    });
  };

// ═══════════════════════════════════════════════════════════
// SLICE
// ═══════════════════════════════════════════════════════════

interface RiderReservationState {
  reservations:       RiderReservation[];   // rider's own trips (used in Home.tsx)
  driverReservations: RiderReservation[];   // driver's assigned trips
  allReservations:    RiderReservation[];   // admin / global view
  reservationById:    RiderReservation | null;
  riderCredit:        RiderCredit | null;
  availability: {
    available: boolean;
    conflict: RiderReservation | null;
  };
  loading: boolean;
  error:   string | null;
}

const initialState: RiderReservationState = {
  reservations:       [],
  driverReservations: [],
  allReservations:    [],
  reservationById:    null,
  riderCredit:        null,
  availability: {
    available: true,
    conflict:  null,
  },
  loading: false,
  error:   null,
};

const riderReservationSlice = createSlice({
  name: "riderReservations",
  initialState,
  reducers: {
    setRiderReservations: (state, action: PayloadAction<RiderReservation[]>) => {
      state.reservations = action.payload;
    },
    setDriverReservations: (state, action: PayloadAction<RiderReservation[]>) => {
      state.driverReservations = action.payload;
    },
    setAllReservations: (state, action: PayloadAction<RiderReservation[]>) => {
      state.allReservations = action.payload;
    },
    setReservationById: (state, action: PayloadAction<RiderReservation | null>) => {
      state.reservationById = action.payload;
    },
    setRiderCredit: (state, action: PayloadAction<RiderCredit>) => {
      state.riderCredit = action.payload;
    },
    setAvailability: (
      state,
      action: PayloadAction<{ available: boolean; conflict: RiderReservation | null }>
    ) => {
      state.availability = action.payload;
    },
    clearRiderReservations: (state) => {
      state.reservations       = [];
      state.driverReservations = [];
      state.allReservations    = [];
      state.reservationById    = null;
      state.riderCredit        = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* upsert */
      .addCase(upsertRiderReservation.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(upsertRiderReservation.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.reservations.findIndex((r) => r.id === action.payload.id);
        if (idx >= 0) {
          state.reservations[idx] = action.payload;
        } else {
          state.reservations.push(action.payload);
        }
      })
      .addCase(upsertRiderReservation.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload ?? "Failed to upsert reservation";
      })

      /* reschedule */
      .addCase(rescheduleRiderReservation.fulfilled, (state, action) => {
        const idx = state.reservations.findIndex((r) => r.id === action.payload.id);
        if (idx >= 0) state.reservations[idx] = action.payload;
      })

      /* cancel */
      .addCase(cancelRiderReservation.fulfilled, (state, action) => {
        const idx = state.reservations.findIndex((r) => r.id === action.payload);
        if (idx >= 0) {
          state.reservations[idx].isCancelled = true;
          state.reservations[idx].status      = "cancelled";
        }
      })

      /* availability check */
      .addCase(checkRiderDateAvailability.fulfilled, (state, action) => {
        state.availability = {
          available: action.payload.available,
          conflict:  action.payload.conflict ?? null,
        };
      });
  },
});

export const {
  setRiderReservations,
  setDriverReservations,
  setAllReservations,
  setReservationById,
  setRiderCredit,
  setAvailability,
  clearRiderReservations,
} = riderReservationSlice.actions;

export default riderReservationSlice.reducer;